/**
 * ExportModal — the editor's Export menu, per mock-up addendum §1. Opens as the
 * design-system `.modal` (composed from `.modal-overlay`/`.modal`/`.modal-header`/
 * `.modal-body`/`.modal-footer` — no new component) listing each export format as
 * a row: icon + label + one-line description on the left, an action button on the
 * right.
 *
 * Wired formats and how each is delivered:
 *   - CSV / GeoJSON / Google Ads / Meta drop-pins → a real file download (GET
 *     .../export.csv | export.geojson | export/google-ads | export/meta-pins) via
 *     downloadFromApi(); the browser handles the attachment and the HttpOnly auth
 *     cookie rides along on the same-origin request.
 *   - PDF → POST .../export/pdf (the route takes an OPTIONAL map PNG; the client
 *     doesn't capture one yet — that's the still-open thumbnail-capture gap — so we
 *     POST with no body and the server returns a legend + area-list PDF). Delivered
 *     via postForDownload() (fetch → blob → save), since it's a POST, not a GET the
 *     browser can navigate to.
 *   - Meta ZIP list → "Copy" (addendum), not a download: fetchText() pulls the
 *     plain one-ZIP-per-line text and writes it to the clipboard for pasting into
 *     Meta Ads Manager's bulk-location box. Falls back to a file download if the
 *     clipboard API is unavailable (e.g. non-secure context).
 *
 * PNG (map image) stays a disabled "Soon" row: it's a client-side canvas capture
 * (decision 5) that isn't built yet — shown so the menu is complete but not faked.
 *
 * v1 exports the WHOLE map only (no group-scoped export — v1.1 per the addendum).
 * The ZCTA≠ZIP caveat (QA R5) shows as a footer note; the CSV/GeoJSON files also
 * carry it inside the file (server-side), so it can't be lost after download.
 */

import React, { useState } from 'react';
import { Image, Table, Braces, FileText, Target, List, MapPin, X, Download, Copy } from 'lucide-react';
import { downloadFromApi, postForDownload, fetchText } from '../utils/api';
import { useToast } from '../context/ToastContext';

// How a format is delivered — drives which handler its button calls and its label.
//   'get'   → GET file download (downloadFromApi)
//   'post'  → POST file download (postForDownload) — for routes with an optional body
//   'copy'  → fetch text + copy to clipboard (fetchText)
//   'soon'  → not built yet (disabled)
const DELIVERY = { GET: 'get', POST: 'post', COPY: 'copy', SOON: 'soon' };

// Export rows in the addendum's order. `endpoint` is relative to the API base.
const FORMATS = [
  {
    key: 'png',
    icon: Image,
    label: 'PNG (map image)',
    description: 'Snapshot of the current map view, 2× resolution',
    delivery: DELIVERY.SOON,
  },
  {
    key: 'csv',
    icon: Table,
    label: 'CSV',
    description: 'All groups, one row per area, includes GEOID + population',
    delivery: DELIVERY.GET,
    endpoint: (id) => `/maps/${id}/export.csv`,
  },
  {
    key: 'geojson',
    icon: Braces,
    label: 'GeoJSON',
    description: 'Full geometry + group/color properties',
    delivery: DELIVERY.GET,
    endpoint: (id) => `/maps/${id}/export.geojson`,
  },
  {
    key: 'pdf',
    icon: FileText,
    label: 'PDF',
    description: 'Printable map + legend',
    delivery: DELIVERY.POST,
    endpoint: (id) => `/maps/${id}/export/pdf`,
  },
  {
    key: 'google-ads',
    icon: Target,
    label: 'Google Ads geo-targeting',
    description: 'ZIP/city/radius targets formatted for Google Ads Editor import',
    delivery: DELIVERY.GET,
    endpoint: (id) => `/maps/${id}/export/google-ads`,
  },
  {
    key: 'meta-zips',
    icon: List,
    label: 'Meta ZIP list',
    description: 'Plain ZIP list, copy-paste ready for Meta Ads Manager',
    delivery: DELIVERY.COPY,
    endpoint: (id) => `/maps/${id}/export/meta-zips`,
  },
  {
    key: 'meta-pins',
    icon: MapPin,
    label: 'Meta drop-pins',
    description: "Radius areas as center + radius pins for Meta's pin-drop targeting",
    delivery: DELIVERY.GET,
    endpoint: (id) => `/maps/${id}/export/meta-pins`,
  },
];

/**
 * @param {object} props
 * @param {string} props.mapId - the map being exported
 * @param {() => void} props.onClose - close the modal
 */
export default function ExportModal({ mapId, onClose }) {
  const { showToast } = useToast();
  // The key of the format currently downloading (for the async POST/copy paths),
  // so its button can show a disabled/pending state and can't be double-fired.
  const [busyKey, setBusyKey] = useState(null);

  // GET downloads: hand off to the browser (server sets Content-Disposition).
  const handleGet = (fmt) => {
    downloadFromApi(fmt.endpoint(mapId));
  };

  // POST download (PDF): fetch the blob and save it. Toast on failure.
  const handlePost = async (fmt) => {
    setBusyKey(fmt.key);
    try {
      await postForDownload(fmt.endpoint(mapId));
    } catch (err) {
      showToast(err.message || 'Export failed', { variant: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  // Copy (Meta ZIPs): fetch the plain text, write to clipboard, toast the count.
  // Falls back to a file download when the clipboard API isn't available.
  const handleCopy = async (fmt) => {
    setBusyKey(fmt.key);
    try {
      const text = await fetchText(fmt.endpoint(mapId));
      if (!text.trim()) {
        showToast('No ZIP-code areas in this map to copy', { variant: 'info' });
        return;
      }
      const zipCount = text.split('\n').filter(Boolean).length;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showToast(`Copied ${zipCount} ZIP${zipCount === 1 ? '' : 's'} to clipboard`);
      } else {
        // No clipboard access (insecure context) — fall back to a download.
        downloadFromApi(fmt.endpoint(mapId));
        showToast(`Downloaded ${zipCount} ZIP${zipCount === 1 ? '' : 's'}`, { variant: 'info' });
      }
    } catch (err) {
      showToast(err.message || 'Copy failed', { variant: 'error' });
    } finally {
      setBusyKey(null);
    }
  };

  const handleAction = (fmt) => {
    if (busyKey) return; // one export at a time
    if (fmt.delivery === DELIVERY.GET) handleGet(fmt);
    else if (fmt.delivery === DELIVERY.POST) handlePost(fmt);
    else if (fmt.delivery === DELIVERY.COPY) handleCopy(fmt);
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Export map"
      // Click on the backdrop (not the modal itself) closes it.
      onClick={onClose}
    >
      <div className="modal export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header export-modal-header">
          <h2 className="modal-title">Export map</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </div>

        <div className="modal-body export-list">
          {FORMATS.map((fmt) => {
            const Icon = fmt.icon;
            const isCopy = fmt.delivery === DELIVERY.COPY;
            const isSoon = fmt.delivery === DELIVERY.SOON;
            const isBusy = busyKey === fmt.key;
            return (
              <div className="export-row" key={fmt.key}>
                <span className="export-row-icon" aria-hidden="true">
                  <Icon size={16} />
                </span>
                <div className="export-row-text">
                  <div className="export-row-label">{fmt.label}</div>
                  <div className="export-row-desc">{fmt.description}</div>
                </div>
                {isSoon ? (
                  <button className="btn btn-outline btn-sm" disabled aria-disabled="true">
                    Soon
                  </button>
                ) : (
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => handleAction(fmt)}
                    disabled={isBusy || (busyKey && !isBusy)}
                  >
                    {isCopy ? <Copy size={13} /> : <Download size={13} />}{' '}
                    {isBusy ? 'Working…' : isCopy ? 'Copy' : 'Download'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="modal-footer export-modal-footer">
          {/* QA R5 caveat — also embedded in the downloaded files server-side. */}
          <p className="export-caveat">
            ZIP-code areas are Census ZCTAs, an approximation of USPS ZIP codes.
          </p>
        </div>
      </div>
    </div>
  );
}
